import { FormEvent, useEffect, useMemo, useState } from "react";
import { Check, ChevronDown, Pencil, Plus, Tags, Trash2, X } from "lucide-react";
import { NotificationModal } from "@/components/modals";
import { useAuth } from "@/contexts/AuthContext";
import { useNotification } from "@/contexts/NotificationContext";
import { api, del, errorMessage, post, put } from "@/lib/api";
import {
  COURSE_DIALOG_STYLE,
  taxonomyKindFromType,
  taxonomyKindLabel,
  type TaxonomyKind,
  type TaxonomyOption,
} from "./courseMeta";

type EditorMode = "create" | "edit";
type TaxonomyCatalog = "primary" | "standard";

interface EditorForm {
  title: string;
  shortTitle: string;
  description: string;
  type: TaxonomyKind;
  parentId: string;
  isGlobal: boolean;
}

/**
 * New entries are always created shared. Learn has no organization picker, and the
 * scope of an existing row is only changeable in the SIS Taxonomy Manager.
 */
function emptyForm(catalog: TaxonomyCatalog): EditorForm {
  return {
    title: "",
    shortTitle: "",
    description: "",
    type: catalog === "standard" ? "standard" : "subject",
    parentId: "",
    isGlobal: true,
  };
}

function idList(value: string[] | undefined) {
  return (value || []).map(String).filter(Boolean);
}

/** A standard belongs to a Core via parents, or a Concentration via pathways. */
function standardInScope(row: TaxonomyOption, scopeId: string) {
  const parents = idList(row.parents);
  const pathways = idList(row.pathways);
  if (parents.includes(scopeId) || pathways.includes(scopeId)) return true;
  return parents.length === 0 && pathways.length === 0;
}

function parentIdForRow(row: TaxonomyOption, scopeId: string, scopeOptions: TaxonomyOption[]) {
  const scopeIds = new Set(scopeOptions.map((item) => String(item.id)));
  const parents = idList(row.parents);
  const pathways = idList(row.pathways);
  if (scopeId && (parents.includes(scopeId) || pathways.includes(scopeId))) return scopeId;
  const fromScope = [...parents, ...pathways].find((id) => scopeIds.has(id));
  return fromScope || parents[0] || pathways[0] || "";
}

function refsForParent(
  parentId: string,
  parentType: string | undefined,
  existing: TaxonomyOption | undefined,
  scopeOptions: TaxonomyOption[],
) {
  const scopeIds = new Set(scopeOptions.map((item) => String(item.id)));
  const keepParents = idList(existing?.parents).filter((id) => !scopeIds.has(id) && id !== parentId);
  const keepPathways = idList(existing?.pathways).filter((id) => !scopeIds.has(id) && id !== parentId);
  if (parentType === "concentration") {
    return { parents: keepParents, pathways: [...keepPathways, parentId] };
  }
  return { parents: [...keepParents, parentId], pathways: keepPathways };
}

function scopeLabel(row: TaxonomyOption) {
  return row.organizationGroupId == null ? "Shared" : "Organization";
}

/** Cores first, then concentrations, alphabetical inside each group. */
function compareOptions(a: TaxonomyOption, b: TaxonomyOption) {
  if (a.type !== b.type) return a.type === "subject" ? -1 : 1;
  return a.title.localeCompare(b.title);
}

export default function TaxonomyPicker({
  selectedId = "",
  selectedIds,
  options,
  loading,
  disabled = false,
  catalog = "primary",
  multiple = false,
  scopeId = "",
  scopeOptions = [],
  allowEditor = true,
  startClosed = false,
  emptyLabel,
  clearable = false,
  onSelect,
  onSelectIds,
  onOptionsChange,
  onEditorOpenChange,
  listOpen: listOpenProp,
  onListOpenChange,
}: {
  selectedId?: string;
  selectedIds?: string[];
  options: TaxonomyOption[];
  loading: boolean;
  disabled?: boolean;
  catalog?: TaxonomyCatalog;
  multiple?: boolean;
  /** Selected Core / Concentration — standards list filters to this id. */
  scopeId?: string;
  scopeOptions?: TaxonomyOption[];
  /** Off when this picker is only choosing an existing row (e.g. parent on a new standard). */
  allowEditor?: boolean;
  /** Empty pickers stay a button until opened (item editor). Course create still auto-opens. */
  startClosed?: boolean;
  emptyLabel?: string;
  clearable?: boolean;
  onSelect?: (id: string) => void;
  onSelectIds?: (ids: string[]) => void;
  onOptionsChange?: (options: TaxonomyOption[]) => void;
  onEditorOpenChange?: (open: boolean) => void;
  listOpen?: boolean;
  onListOpenChange?: (open: boolean) => void;
}) {
  const { capabilities } = useAuth();
  const { toast, notice } = useNotification();
  const canCreate = allowEditor && Boolean(capabilities?.createTaxonomy);
  const canEdit = allowEditor && Boolean(capabilities?.editTaxonomy);
  const canRemove = allowEditor && Boolean(capabilities?.removeTaxonomy);

  const pickedIds = multiple ? selectedIds ?? [] : selectedId ? [selectedId] : [];
  const [search, setSearch] = useState("");
  const [internalListOpen, setInternalListOpen] = useState(
    pickedIds.length === 0 && !startClosed,
  );
  const listOpen = listOpenProp ?? internalListOpen;
  const setListOpen = (next: boolean | ((open: boolean) => boolean)) => {
    const value = typeof next === "function" ? next(listOpen) : next;
    onListOpenChange?.(value);
    if (listOpenProp === undefined) setInternalListOpen(value);
  };
  const [editor, setEditor] = useState<EditorMode | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<EditorForm>(() => emptyForm(catalog));
  const [busy, setBusy] = useState(false);

  const selectedRow = useMemo(
    () => options.find((row) => String(row.id) === String(pickedIds[0] || "")) || null,
    [options, pickedIds],
  );
  const selectedRows = useMemo(
    () => options.filter((row) => pickedIds.includes(String(row.id))),
    [options, pickedIds],
  );

  const scoped = useMemo(() => {
    const rows = [...options].sort(compareOptions);
    if (catalog !== "standard") return rows;
    if (!scopeId) return [];
    return rows.filter((row) => standardInScope(row, scopeId));
  }, [options, catalog, scopeId]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return scoped;
    return scoped.filter((row) => {
      const title = row.title.toLowerCase();
      const shortTitle = String(row.shortTitle || "").toLowerCase();
      const desc = String(row.description || "").toLowerCase();
      return title.includes(q) || shortTitle.includes(q) || desc.includes(q);
    });
  }, [scoped, search]);

  const parentChoices = useMemo(() => [...scopeOptions].sort(compareOptions), [scopeOptions]);
  const parentRow = parentChoices.find((row) => row.id === form.parentId) || null;

  useEffect(() => {
    onEditorOpenChange?.(Boolean(editor));
  }, [editor, onEditorOpenChange]);

  useEffect(() => {
    return () => onEditorOpenChange?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const catalogQuery = catalog === "standard" ? "?type=standard" : "";
  const noun = catalog === "standard" ? "standard" : "core or concentration";
  const searchLabel = catalog === "standard" ? "Search standards…" : "Search cores and concentrations…";
  const listLabel = catalog === "standard" ? "Standards" : "Cores and concentrations";

  const refresh = async () => {
    const envelope = await api<{ success: boolean; data: TaxonomyOption[] }>(
      `/api/courses/taxonomy${catalogQuery}`,
    );
    const next = Array.isArray(envelope.data) ? envelope.data : [];
    onOptionsChange?.(next);
    return next;
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm(catalog), parentId: catalog === "standard" ? scopeId || "" : "" });
    setEditor("create");
  };

  const openEdit = (row: TaxonomyOption) => {
    setEditingId(row.id);
    setForm({
      title: row.title || "",
      shortTitle: row.shortTitle || "",
      description: row.description || "",
      type: taxonomyKindFromType(row.type),
      parentId: catalog === "standard" ? parentIdForRow(row, scopeId, scopeOptions) : "",
      isGlobal: row.organizationGroupId == null,
    });
    setEditor("edit");
  };

  const pick = (id: string) => {
    if (multiple) {
      const next = pickedIds.includes(id) ? pickedIds.filter((item) => item !== id) : [...pickedIds, id];
      onSelectIds?.(next);
      return;
    }
    onSelect?.(id);
    if (id) setListOpen(false);
  };

  const closeEditor = () => {
    if (busy) return;
    setEditor(null);
    setEditingId(null);
    setForm(emptyForm(catalog));
  };

  const handleSave = async (event: FormEvent) => {
    // Portaled, but React still bubbles submit up the component tree into the
    // create/edit course form. Stop that so Save here never creates the course.
    event.preventDefault();
    event.stopPropagation();
    if (!form.title.trim()) return;
    if (catalog === "standard" && !form.parentId) return;
    const kindLabel = taxonomyKindLabel(form.type);
    const existing = editingId ? options.find((row) => row.id === editingId) : undefined;
    const refs =
      catalog === "standard"
        ? refsForParent(form.parentId, parentRow?.type, existing, scopeOptions)
        : null;
    setBusy(true);
    try {
      if (editor === "edit" && editingId) {
        // isGlobal is left out on purpose — scope changes belong to the SIS manager.
        await put(`/api/courses/taxonomy/${encodeURIComponent(editingId)}`, {
          title: form.title.trim(),
          shortTitle: form.shortTitle.trim() || null,
          description: form.description.trim() || null,
          type: form.type,
          ...(refs || {}),
        });
        toast.success(`${kindLabel} updated`);
        await refresh();
        closeEditor();
      } else {
        const created = await post<{ success: boolean; data: TaxonomyOption; id?: string }>(
          "/api/courses/taxonomy",
          {
            title: form.title.trim(),
            shortTitle: form.shortTitle.trim() || null,
            description: form.description.trim() || null,
            type: form.type,
            isGlobal: true,
            ...(refs || {}),
          },
        );
        toast.success(`${kindLabel} created`);
        await refresh();
        const newId = created.data?.id || created.id;
        if (newId) pick(String(newId));
        closeEditor();
      }
    } catch (err) {
      notice({
        type: "error",
        title:
          editor === "edit"
            ? `Could not update ${kindLabel.toLowerCase()}`
            : `Could not create ${kindLabel.toLowerCase()}`,
        subtext: errorMessage(err),
        duration: 7000,
      });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (row: TaxonomyOption) => {
    if (
      !window.confirm(
        `Delete “${row.title}”?\n\nThis removes the catalog entry in UIS. Courses, SIS forms, and other products that used it will lose the link.`,
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      await del(`/api/courses/taxonomy/${encodeURIComponent(row.id)}`);
      toast.success(`${taxonomyKindLabel(row.type)} deleted`);
      if (pickedIds.includes(row.id)) {
        if (multiple) onSelectIds?.(pickedIds.filter((id) => id !== row.id));
        else onSelect?.("");
        setListOpen(true);
      }
      await refresh();
    } catch (err) {
      notice({
        type: "error",
        title: "Could not delete that entry",
        subtext: errorMessage(err),
        duration: 7000,
      });
    } finally {
      setBusy(false);
    }
  };

  const rowsBusy = busy || disabled;
  const hasSelection = pickedIds.length > 0;
  const showList = listOpen || (!hasSelection && !startClosed);
  const choiceTitle =
    selectedRows.length > 1
      ? `${selectedRows.length} standards`
      : selectedRow?.title || (loading ? "Loading…" : "Selected category");
  const choiceType = selectedRow ? taxonomyKindLabel(selectedRow.type) : catalog === "standard" ? "Standard" : null;

  return (
    <>
      {hasSelection ? (
        <button
          type="button"
          className="al-tax-choice"
          aria-haspopup="listbox"
          aria-expanded={showList}
          title={catalog === "standard" ? "Change standards" : "Change category"}
          disabled={rowsBusy}
          onClick={() => setListOpen((open) => !open)}
        >
          <span className="al-tax-choice-title">{choiceTitle}</span>
          {choiceType ? <span className="al-tax-choice-type">{choiceType}</span> : null}
          {clearable && !disabled ? (
            <span
              role="button"
              tabIndex={-1}
              className="al-tax-choice-clear"
              aria-label="Clear category"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onSelect?.("");
                setListOpen(false);
              }}
            >
              <X size={12} aria-hidden="true" />
            </span>
          ) : (
            <ChevronDown size={14} aria-hidden="true" />
          )}
        </button>
      ) : startClosed ? (
        <button
          type="button"
          className="al-tax-choice al-tax-choice--empty"
          aria-haspopup="listbox"
          aria-expanded={showList}
          disabled={rowsBusy}
          onClick={() => setListOpen((open) => !open)}
        >
          <span className="al-tax-choice-title">{emptyLabel || searchLabel}</span>
          <ChevronDown size={14} aria-hidden="true" />
        </button>
      ) : null}

      {showList ? (
      <div className="al-tax-panel">
        <div className="al-tax-panel-head">
          <input
            type="search"
            className="al-tax-search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={searchLabel}
            aria-label={searchLabel}
            disabled={rowsBusy}
          />
          {canCreate ? (
            <button
              type="button"
              className="al-tax-new"
              title={`New ${noun}`}
              aria-label={`New ${noun}`}
              disabled={rowsBusy}
              onClick={openCreate}
            >
              <Plus size={14} aria-hidden="true" />
            </button>
          ) : null}
        </div>

        <div className="al-tax-rows" role="listbox" aria-label={listLabel} aria-multiselectable={multiple}>
          {loading ? (
            <p className="al-tax-status">
              <span className="al-tax-spinner" aria-hidden="true" />
              Loading…
            </p>
          ) : visible.length === 0 ? (
            <p className="al-tax-status">
              {catalog === "standard" && !scopeId
                ? "Pick a subject above to see its standards."
                : scoped.length === 0
                  ? catalog === "standard"
                    ? "No standards for this subject yet."
                    : "Nothing in the catalog yet."
                  : "No match for that search."}
            </p>
          ) : (
            visible.map((row) => {
              const isSelected = pickedIds.includes(String(row.id));
              return (
                <div
                  key={row.id}
                  className={`al-tax-row${isSelected ? " al-tax-row--active" : ""}`}
                  role="option"
                  aria-selected={isSelected}
                >
                  <button
                    type="button"
                    className="al-tax-row-main"
                    title={`${row.title} — ${scopeLabel(row)}`}
                    disabled={rowsBusy}
                    onClick={() => pick(row.id)}
                  >
                    <span className="al-tax-row-check" aria-hidden="true">
                      {isSelected ? <Check size={12} /> : null}
                    </span>
                    <span className="al-tax-row-title">{row.title}</span>
                    <span className="al-tax-row-type">{taxonomyKindLabel(row.type)}</span>
                  </button>
                  {canEdit || canRemove ? (
                    <span className="al-tax-row-actions">
                      {canEdit ? (
                        <button
                          type="button"
                          className="al-tax-icon"
                          title={`Edit ${row.title}`}
                          aria-label={`Edit ${row.title}`}
                          disabled={rowsBusy}
                          onClick={() => openEdit(row)}
                        >
                          <Pencil size={12} aria-hidden="true" />
                        </button>
                      ) : null}
                      {canRemove ? (
                        <button
                          type="button"
                          className="al-tax-icon al-tax-icon--danger"
                          title={`Delete ${row.title}`}
                          aria-label={`Delete ${row.title}`}
                          disabled={rowsBusy}
                          onClick={() => void handleDelete(row)}
                        >
                          <Trash2 size={12} aria-hidden="true" />
                        </button>
                      ) : null}
                    </span>
                  ) : null}
                </div>
              );
            })
          )}
        </div>

        {allowEditor && !canCreate ? (
          <p className="al-tax-perm-hint">
            Adding an entry needs taxonomy.CreateTaxonomy on AcuityLearn.
          </p>
        ) : null}
      </div>
      ) : null}

      <NotificationModal
        isOpen={Boolean(editor)}
        type="neutral"
        icon={<Tags />}
        title={editor === "edit" ? "Edit catalog entry" : "New catalog entry"}
        message={null}
        overlay
        overlayClose={!busy}
        escapeClose={!busy}
        showOkButton={false}
        showCloseButton={!busy}
        align="center"
        iconSize="2.25rem"
        titleSize="1.05rem"
        zIndex={1300}
        style={COURSE_DIALOG_STYLE}
        onClose={closeEditor}
      >
        <form
          className="al-tax-editor"
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void handleSave(event);
          }}
        >
          <span className="al-courses-label">Type</span>
          {catalog === "standard" ? (
            <span className="al-courses-badge al-courses-badge--on">Standard</span>
          ) : (
          <div className="al-courses-kind" role="group" aria-label="Entry type">
            <button
              type="button"
              className={
                form.type === "subject"
                  ? "al-courses-badge al-courses-badge--on al-courses-badge--clickable"
                  : "al-courses-badge al-courses-badge--clickable"
              }
              aria-pressed={form.type === "subject"}
              disabled={busy}
              onClick={() => setForm((current) => ({ ...current, type: "subject" }))}
            >
              Core
            </button>
            <button
              type="button"
              className={
                form.type === "concentration"
                  ? "al-courses-badge al-courses-badge--on al-courses-badge--clickable"
                  : "al-courses-badge al-courses-badge--clickable"
              }
              aria-pressed={form.type === "concentration"}
              disabled={busy}
              onClick={() => setForm((current) => ({ ...current, type: "concentration" }))}
            >
              Concentration
            </button>
          </div>
          )}

          {catalog === "standard" ? (
            <>
              <span className="al-courses-label">Subject</span>
              <TaxonomyPicker
                selectedId={form.parentId}
                options={parentChoices}
                loading={false}
                disabled={busy}
                allowEditor={false}
                onSelect={(id) => setForm((current) => ({ ...current, parentId: id }))}
              />
              <p className="al-courses-hint">
                Required. A Core is stored as the hierarchy parent; a Concentration as the pathway.
              </p>
            </>
          ) : null}

          <label className="al-courses-label" htmlFor="tax-entry-title">
            Title
          </label>
          <input
            id="tax-entry-title"
            className="al-courses-input"
            value={form.title}
            onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
            placeholder={
              form.type === "concentration"
                ? "e.g. Health Science"
                : form.type === "standard"
                  ? "e.g. HSS 10.2"
                  : "e.g. Mathematics"
            }
            required
            disabled={busy}
            autoFocus
          />
          <label className="al-courses-label" htmlFor="tax-entry-short">
            Short title
          </label>
          <input
            id="tax-entry-short"
            className="al-courses-input"
            value={form.shortTitle}
            onChange={(event) => setForm((current) => ({ ...current, shortTitle: event.target.value }))}
            placeholder="Optional — e.g. Math"
            disabled={busy}
          />
          <label className="al-courses-label" htmlFor="tax-entry-desc">
            Description
          </label>
          <textarea
            id="tax-entry-desc"
            className="al-courses-textarea"
            value={form.description}
            onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
            placeholder="Optional notes"
            rows={3}
            disabled={busy}
          />
          <label className="al-courses-check">
            <input type="checkbox" checked={form.isGlobal} disabled readOnly />
            <span>
              Shared across all organizations
              <span className="al-courses-radio-hint">
                {editor === "edit"
                  ? "Scope is set in the SIS Taxonomy Manager and cannot be changed here."
                  : "Entries added from Learn are always shared."}
              </span>
            </span>
          </label>
          <div className="al-courses-assign-actions">
            <button
              type="button"
              className="al-courses-btn al-courses-btn--ghost"
              onClick={closeEditor}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="al-courses-btn"
              disabled={busy || !form.title.trim() || (catalog === "standard" && !form.parentId)}
            >
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </NotificationModal>
    </>
  );
}
